/**
 * OwnerPersonForm — Bloc partagé "Propriétaire particulier / société".
 *
 * Utilisé par :
 *  - le Wizard de création de bien (Step4_Owner) → mode="wizard"
 *  - la Fiche d'édition d'un bien (PropertyEditPage → Nouveau propriétaire) → mode="edit"
 *
 * Objectif : maintenir la parité entre wizard et fiche édition pour les champs Particulier/Société
 * (forme juridique, raison sociale, SIREN/RCS, siège social, représentant, notes, etc.).
 *
 * Contrat :
 *  - `value`   : objet complet (voir DEFAULT_VALUE) — la source de vérité est le parent.
 *  - `onChange`: (nextValue) => void — remplace l'objet complet (immuable).
 *  - `mode`    : 'wizard' | 'edit' — pilote la palette (blue/red) et l'affichage adresse détaillée.
 *  - `showAddress` : bool — affiche le pavé Adresse/CP/Ville/Pays (wizard = true, edit = false par défaut).
 *  - `showNotes`   : bool — affiche le champ Notes internes (edit = true, wizard = false).
 */

import React from 'react';
import { FORME_JURIDIQUE_OPTIONS } from '../../constants/contactEnums';
import sortSelectOptions from '../../utils/sortSelectOptions';
import { estPersonneMorale } from '../wizard/reglesEtapes';

export const DEFAULT_OWNER_VALUE = {
  type: 'personne_physique',
  civilite: '',
  nom: '',
  prenom: '',
  societe: '',
  telephone: '',
  email: '',
  adresse: '',
  code_postal: '',
  ville: '',
  pays: 'France',
  notes: '',
  // Société (personne morale)
  forme_juridique: '',
  siren_rcs: '',
  siege_social: '',
  representant: '',
  telephone_pro: '',
  email_pro: '',
};

const OwnerPersonForm = ({
  value,
  onChange,
  mode = 'edit',
  showAddress = false,
  showNotes = true,
}) => {
  const v = { ...DEFAULT_OWNER_VALUE, ...(value || {}) };
  // Même définition que la règle de validation (lot Z1) : tolère les valeurs héritées
  // `particulier` (wizard) et `societe` (extraction IA d'un mandat).
  const isPhysique = !estPersonneMorale(v);

  // Palette selon contexte : wizard = bleu (cohérent avec les autres steps), edit = rouge (identité PropertyEditPage)
  const accent = mode === 'wizard'
    ? { text: 'text-blue-600', ring: 'focus:ring-blue-500', ringPrimary: 'focus:ring-2 focus:ring-blue-500' }
    : { text: 'text-red-600', ring: 'focus:ring-red-500', ringPrimary: 'focus:ring-2 focus:ring-red-500' };

  const set = (patch) => onChange({ ...v, ...patch });

  return (
    <div className="space-y-4">
      {/* Type */}
      <div>
        <label className="block text-xs font-medium text-slate-900 mb-2">
          Type <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              data-testid="owner-type-physique-radio"
              checked={isPhysique}
              onChange={() => set({ type: 'personne_physique' })}
              className={accent.text}
            />
            <span className="text-sm">Personne physique</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              data-testid="owner-type-morale-radio"
              checked={!isPhysique}
              onChange={() => set({ type: 'personne_morale' })}
              className={accent.text}
            />
            <span className="text-sm">Personne morale (société)</span>
          </label>
        </div>
      </div>

      {/* Nom selon type */}
      {isPhysique ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-900 mb-2">Civilité</label>
            <select
              data-testid="owner-civilite-select"
              value={v.civilite || ''}
              onChange={(e) => set({ civilite: e.target.value })}
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white ${accent.ringPrimary}`}
            >
              <option value="">—</option>
              <option value="M.">M.</option>
              <option value="Mme">Mme</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-900 mb-2">
              Prénom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              data-testid="owner-prenom-input"
              value={v.prenom || ''}
              onChange={(e) => set({ prenom: e.target.value })}
              placeholder="Jean"
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-900 mb-2">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              data-testid="owner-nom-input"
              value={v.nom || ''}
              onChange={(e) => set({ nom: e.target.value })}
              placeholder="Dupont"
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-900 mb-2">
              Raison sociale <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              data-testid="owner-societe-input"
              value={v.societe || ''}
              onChange={(e) => set({ societe: e.target.value })}
              placeholder="SCI Les Jardins"
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-900 mb-2">Forme juridique</label>
              <select
                data-testid="owner-forme-juridique-select"
                value={v.forme_juridique || ''}
                onChange={(e) => set({ forme_juridique: e.target.value })}
                className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white ${accent.ringPrimary}`}
              >
                <option value="">—</option>
                {sortSelectOptions(FORME_JURIDIQUE_OPTIONS).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-900 mb-2">SIREN / RCS</label>
              <input
                type="text"
                data-testid="owner-siren-rcs-input"
                value={v.siren_rcs || ''}
                onChange={(e) => set({ siren_rcs: e.target.value })}
                placeholder="123 456 789"
                className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-900 mb-2">Siège social</label>
            <input
              type="text"
              data-testid="owner-siege-social-input"
              value={v.siege_social || ''}
              onChange={(e) => set({ siege_social: e.target.value })}
              placeholder="12 rue de la Paix, 75002 Paris"
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-900 mb-2">Représentant légal</label>
            <input
              type="text"
              data-testid="owner-representant-input"
              value={v.representant || ''}
              onChange={(e) => set({ representant: e.target.value })}
              placeholder="Nom du gérant / dirigeant"
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
            />
          </div>
        </div>
      )}

      {/* Contacts — la règle exige au moins un des deux (téléphone OU email) : les deux
          portent donc l'étoile, avec la précision juste en dessous (lot Z2). */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-900 mb-2">
            Téléphone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            data-testid="owner-telephone-input"
            value={v.telephone || ''}
            onChange={(e) => set({ telephone: e.target.value })}
            placeholder="+33 6 12 34 56 78"
            className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-900 mb-2">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            data-testid="owner-email-input"
            value={v.email || ''}
            onChange={(e) => set({ email: e.target.value })}
            placeholder="contact@example.com"
            className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
          />
        </div>
      </div>
      <p className="text-[11px] text-slate-500 -mt-2">
        Au moins un des deux (téléphone ou email) est obligatoire.
      </p>

      {/* Adresse (optionnel — wizard uniquement par défaut) */}
      {showAddress && (
        <>
          <div>
            <label className="block text-xs font-medium text-slate-900 mb-2">Adresse</label>
            <input
              type="text"
              data-testid="owner-adresse-input"
              value={v.adresse || ''}
              onChange={(e) => set({ adresse: e.target.value })}
              placeholder="12 rue de la République"
              className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-900 mb-2">Code postal</label>
              <input
                type="text"
                data-testid="owner-code-postal-input"
                value={v.code_postal || ''}
                onChange={(e) => set({ code_postal: e.target.value })}
                placeholder="06000"
                className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-900 mb-2">Ville</label>
              <input
                type="text"
                data-testid="owner-ville-input"
                value={v.ville || ''}
                onChange={(e) => set({ ville: e.target.value })}
                placeholder="Nice"
                className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-900 mb-2">Pays</label>
              <input
                type="text"
                data-testid="owner-pays-input"
                value={v.pays || 'France'}
                onChange={(e) => set({ pays: e.target.value })}
                placeholder="France"
                className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
              />
            </div>
          </div>
        </>
      )}

      {/* Notes (edit uniquement par défaut) */}
      {showNotes && (
        <div>
          <label className="block text-xs font-medium text-slate-900 mb-2">
            Notes internes <span className="text-slate-400">(optionnel)</span>
          </label>
          <textarea
            data-testid="owner-notes-textarea"
            value={v.notes || ''}
            onChange={(e) => set({ notes: e.target.value })}
            placeholder="Remarques internes sur ce propriétaire..."
            rows="2"
            className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm ${accent.ringPrimary}`}
          />
        </div>
      )}
    </div>
  );
};

export default OwnerPersonForm;
